<template>
    <div>
        <div class="shared-assignments-tools">
            <div class="shared-assignment-search apprentice-search-form">
                <b-input-group>
                    <b-form-input
                        v-model="searchTerm"
                        size="md"
                        :placeholder="$t('portfolio.search')"></b-form-input>
                    <b-input-group-append>
                        <div class="icon">
                            <i class="fas fa-search fa-fw"></i>
                        </div>
                    </b-input-group-append>
                </b-input-group>
            </div>

            <div class="shared-assignment-sorting">
                <label for="shared-assignment-sorting-options">{{ $t('portfolio.sort_on') }}</label>
                <b-form-select
                    class="apprentice-primary-select shared-assignments-sort-filter"
                    id="shared-assignment-sorting-options"
                    v-model="selected"
                    :options="options">
                </b-form-select>
            </div>
        </div>

        <div
            class="loading fa-3x"
            v-if="isLoading">
            <i class="fas fa-spinner fa-pulse"></i>
        </div>

        <div
            class="shared-assignments"
            v-else-if="assignments.length">
            <div class="row">
                <div
                    class="col-12 col-lg-6 col-xl-4 shared-assignment-item"
                    v-for="assignment in currentPageCards"
                    :key="assignment.id">
                    <b-card
                        class="shared-assignment"
                        no-body
                        :class="
                            !assignment.assignment.organization_assignment_folder.length && assignment.anonymous
                                ? 'anonymous-assignment'
                                : null
                        ">
                        <div class="assignment-header">
                            <b-card-img :src="banner(assignment)"> </b-card-img>
                            <div class="actions">
                                <div
                                    class="activity"
                                    v-if="assignment.paid">
                                    <span
                                        class="btn btn-primary-icon btn-euro btn-activity btn-paid-activity"
                                        :v-b-tooltip="$t('portfolio.paid_activities')">
                                    </span>
                                </div>
                                <div
                                    class="activity"
                                    v-if="assignment.activity">
                                    <span
                                        class="btn btn-primary-icon btn-running btn-activity btn-external-activity"
                                        :v-b-tooltip="$t('portfolio.activity_with_external_party')">
                                    </span>
                                </div>
                                <div
                                    class="assignment-preview"
                                    v-if="canPreview(assignment)">
                                    <b-button
                                        class="btn-eye btn-preview"
                                        variant="primary-icon"
                                        @click="previewAssignment(assignment.id)"
                                        :v-b-tooltip="$t('portfolio.view_assignment')">
                                    </b-button>
                                </div>
                                <div
                                    class="assignment-video-preview"
                                    v-if="assignment.video !== null">
                                    <b-button
                                        class="btn-video-icon btn-video-preview"
                                        variant="primary-icon"
                                        @click="showAssignmentDescriptionModal(assignment)"
                                        :v-b-tooltip="$t('portfolio.view_video')">
                                    </b-button>
                                </div>
                                <div
                                    class="add-assignment"
                                    v-if="
                                        !$authUser.isStudent() && !previewModeOnly && databankTypeIn === 'assignment'
                                    ">
                                    <b-button
                                        class="btn-add btn-add-assignment"
                                        variant="primary-icon"
                                        @click="addAssignment(assignment.id)"
                                        :v-b-tooltip="$t('portfolio.add_assignment')">
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <b-card-body body-class="shared-assignment-body">
                            <b-card-title title-tag="h4">{{ assignment.name }}</b-card-title>
                            <b-card-text
                                class="shared-assignment-organisation"
                                v-if="assignment.assignment.organization_assignment_folder.length"
                                text-tag="div">
                                {{ assignment.assignment.organization_assignment_folder[0].organization.name }}
                            </b-card-text>

                            <b-card-text
                                class="shared-assignment-teacher"
                                v-else-if="
                                    assignment.publisher && assignment.publisher.full_name && !assignment.anonymous
                                "
                                text-tag="div">
                                {{
                                    assignment.anonymous
                                        ? $t('portfolio.anonymous')
                                        : assignment.publisher.full_name
                                }}
                            </b-card-text>

                            <b-card-text
                                class="shared-assignment-education-levels"
                                v-if="assignment.education_levels.length"
                                text-tag="div">
                                {{
                                    assignment.education_levels.map((educationLevel) => educationLevel.name).join(' | ')
                                }}
                            </b-card-text>

                            <b-card-text
                                class="shared-assignment-category"
                                v-if="assignment.categories.length"
                                text-tag="div">
                                {{ assignment.categories.map((category) => category.name).join(' | ') }}
                            </b-card-text>

                            <b-card-text
                                class="shared-assignment-description"
                                v-if="assignment.description.length"
                                text-tag="div">
                                {{ previewDescription(assignment.description) }}
                            </b-card-text>
                            <div class="more">
                                <b-button
                                    class="btn-link-type btn-show-more show-more-desc"
                                    v-if="assignment.description.length && hasMoreToShow(assignment.description)"
                                    variant="primary"
                                    @click="showAssignmentDescriptionModal(assignment)">
                                    {{ $t('portfolio.show_more') }}
                                </b-button>
                            </div>
                        </b-card-body>
                    </b-card>
                </div>

                <design-2021-shared-assignment-description-modal-component ref="description-assignment-modal">
                </design-2021-shared-assignment-description-modal-component>
            </div>

            <div
                class="apprentice-pagination shared-assignments-pagination"
                v-if="assignments && assignments.length > perPage">
                <b-pagination
                    v-model="currentPage"
                    :total-rows="rows"
                    :per-page="perPage">
                </b-pagination>
            </div>
        </div>
        <div
            class="no-shared-assignments"
            v-else>
            <b-alert
                variant="info"
                show
                >{{ $t('portfolio.no_assignments_shared_yet') }}</b-alert
            >
        </div>
    </div>
</template>

<script>
export default {
    name: 'design-2021-portfolio-edit-shared-assignment-card-component',
    props: {
        assignmentsIn: Array,
        isLoading: Boolean,
        filters: Object,
        databankTypeIn: {
            String,
            default: 'assignment',
        },
        groupIdIn: {
            type: Number,
            default: null,
        },
        portfolioCategoryIdIn: {
            type: Number,
            default: null,
        },
        organizationIdIn: {
            type: Number,
            default: null,
        },
        groupId: Number,
        portfolioCategoryId: Number,
        previewModeOnly: Boolean,
    },
    data() {
        return {
            searchTerm: '',
            options: [
                {
                    value: 1,
                    text: this.$t('common.title'),
                },
                {
                    value: 2,
                    text: this.$t('portfolio.last_added'),
                },
            ],
            selected: 1,
            currentPage: 1,
            perPage: 8,
        };
    },

    computed: {
        assignments() {
            if (!this.filters.categories) {
                return [];
            }

            const assignments = this.assignmentsIn.filter((assignment) => {
                if (!this.filters.activity && assignment.activity) {
                    return false;
                }

                if (this.filters.categories.length) {
                    let check = false;
                    categoryLoop: for (const category_id of this.filters.categories) {
                        for (const assignment_category of assignment.categories) {
                            if (category_id === assignment_category.id) {
                                check = true;
                                break categoryLoop;
                            }
                        }
                    }
                    if (!check) {
                        return false;
                    }
                }

                if (this.filters.courses.length) {
                    let check = false;
                    courseFilterLoop: for (const course_id of this.filters.courses) {
                        for (const assignmentCourse of assignment.courses) {
                            if (assignmentCourse.id === course_id) {
                                check = true;
                                break courseFilterLoop;
                            }
                        }
                        if (assignment.course_id === course_id) {
                            check = true;
                            break;
                        }
                    }
                    if (!check) {
                        return false;
                    }
                }

                if (this.filters.education_levels.length) {
                    let check = false;
                    educationLevelsLoop: for (const education_level_id of this.filters.education_levels) {
                        for (const education_level of assignment.education_levels) {
                            if (education_level_id === education_level.id) {
                                check = true;
                                break educationLevelsLoop;
                            }
                        }
                    }
                    if (!check) {
                        return false;
                    }
                }

                if (this.filters.teachers.length) {
                    if (assignment.anonymous) {
                        return false;
                    }

                    let check = false;
                    for (const teacher_id of this.filters.teachers) {
                        if (assignment.published_by === teacher_id) {
                            check = true;
                            break;
                        }
                    }
                    if (!check) {
                        return false;
                    }
                }

                if (this.filters.organisations.length) {
                    if (!assignment.assignment.organization_assignment_folder.length) {
                        return false;
                    }

                    if (assignment.paid && !this.$authUser.isAdmin()) {
                        return false;
                    }

                    let check = false;
                    for (const organization_id of this.filters.organisations) {
                        if (
                            organization_id === assignment.assignment.organization_assignment_folder[0].organization.id
                        ) {
                            check = true;
                            break;
                        }
                    }
                    if (!check) {
                        return false;
                    }
                }

                if (this.searchTerm.length >= 3) {
                    let check = false;
                    const lowerCasedTerm = this.searchTerm.toLowerCase();

                    if (assignment.name.toLowerCase().includes(lowerCasedTerm)) {
                        check = true;
                    }

                    if (assignment.publisher && assignment.publisher.full_name.toLowerCase().includes(lowerCasedTerm)) {
                        check = true;
                    }

                    if (assignment.description.toLowerCase().includes(lowerCasedTerm)) {
                        check = true;
                    }

                    for (const assignment_category of assignment.categories) {
                        if (assignment_category.name.toLowerCase().includes(lowerCasedTerm)) {
                            check = true;
                            break;
                        }
                    }

                    if (!check) {
                        return false;
                    }
                }

                return true;
            });

            if (this.selected === 1) {
                assignments.sort((a, b) => a.name.localeCompare(b.name));
            }

            if (this.selected === 2) {
                assignments.sort((a, b) => b.created_at.localeCompare(a.created_at));
            }

            return assignments;
        },

        rows() {
            return this.assignments.length;
        },

        currentPageCards() {
            if (this.assignments.length > this.perPage) {
                const start = (this.currentPage - 1) * this.perPage;
                if (this.assignments.length - start > this.perPage) {
                    return this.assignments.slice(start, this.currentPage * this.perPage);
                } else {
                    return this.assignments.slice(start);
                }
            }

            return this.assignments;
        },
    },

    methods: {
        banner(assignment) {
            if (assignment.banner_image.file && assignment.banner_image.file.image) {
                return assignment.banner_image.file.image.thumb_path;
            } else if (assignment.banner_image.file) {
                return assignment.banner_image.file.public_path;
            }

            return '/images/databank_banner_default.jpg';
        },
        addAssignment(assignmentId) {
            const data = {
                group_id: this.groupIdIn,
                portfolio_category_id: this.portfolioCategoryIdIn,
                ab_assignment_id: assignmentId,
            };

            this.alert.saving(
                this.$t('portfolio.add_assignment'),
                this.$t('portfolio.this_assignment_will_be_added_to_the_assignment_list_please_wait_one_moment'),
            );
            this.axios.post(this.routes.portfolioSaveAbAssignment(), data).then(
                (response) => {
                    this.alert.success(
                        this.$t('portfolio.assignment_added'),
                        this.$t('portfolio.the_assignment_is_added_successfully_to_assignment_list'),
                    );
                    this.$emit('add-assignment-to-assignments', response.data);
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        previewAssignment(abAssignmentId) {
            this.$emit('preview-assignment', abAssignmentId);
        },

        showAssignmentDescriptionModal(assignment) {
            const data = [];
            if (assignment.video !== null) {
                data.push({
                    title: assignment.name,
                    description: assignment.description,
                    video: {
                        extension: assignment.video.file.extension,
                        full_name: assignment.video.file.full_name,
                        src: assignment.video.file.public_path,
                        type: `video/${assignment.video.file.extension}`,
                    },
                });
            } else {
                data.push({
                    title: assignment.name,
                    description: assignment.description,
                });
            }

            this.$refs['description-assignment-modal'].show(assignment);
        },
        canPreview(assignment) {
            if (
                (this.databankTypeIn === 'organization' &&
                    assignment.assignment.organization_assignment_folder[0].organization.id ===
                        this.organizationIdIn) ||
                this.databankTypeIn === 'assignment'
            ) {
                return true;
            }

            return false;
        },
        previewDescription(value) {
            const dummyDiv = document.createElement('div');
            dummyDiv.innerHTML = value;
            const contentStripped = dummyDiv.textContent || dummyDiv.innerText || '';
            dummyDiv.remove();
            return contentStripped.split(' ').length > 20
                ? contentStripped.split(' ').splice(0, 20).join(' ') + ' ...'
                : contentStripped;
        },

        hasMoreToShow(value) {
            const dummyDiv = document.createElement('div');
            dummyDiv.innerHTML = value;
            const contentStripped = dummyDiv.textContent || dummyDiv.innerText || '';
            dummyDiv.remove();
            return contentStripped.split(' ').length > 20;
        },
    },
    watch: {
        rows: function (value, oldVal) {
            if (value !== oldVal && value > this.perPage && this.currentPage !== 1) {
                this.currentPage = 1;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.shared-assignment-search {
    border: 1px solid $border-color;
    border-radius: $border-radius-content;
    align-self: flex-end;
}

.shared-assignments-sort-filter {
    margin-bottom: 0;
    border: 1px solid $border-color;
}

.shared-assignments-tools {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-content: baseline;
    padding-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    border-bottom: 1px solid $border-color;
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.actions {
    display: flex;
    justify-content: flex-start;
    align-content: baseline;
    flex-direction: row;
    position: absolute;
    top: map-get($grid-gutter-spacing, 'eight');
    right: map-get($grid-gutter-spacing, 'eight');

    .btn {
        font-size: $default-font-size;
        line-height: 1;

        &:before {
            margin-right: 0;
        }
    }

    > div:not(:last-child) {
        margin-right: map-get($grid-gutter-spacing, 'eight');
    }
}

.shared-assignment-item {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.shared-assignment ::v-deep {
    border: none;
    border-radius: $border-radius-content;

    img {
        height: 100px;
        object-fit: cover;
        object-position: center;
        width: 100%;
        border-radius: 0;
        border-top-left-radius: $border-radius-content;
        border-top-right-radius: $border-radius-content;
    }

    .shared-assignment-body {
        padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');
        display: flex;
        flex-direction: column;

        .card-title {
            margin-bottom: map-get($grid-gutter-spacing, 'ten');
            font-size: $default-font-size;
            font-weight: $font-weight-medium;
            min-height: 32px;
        }
    }

    .more {
        display: flex;
        .btn {
            justify-content: flex-end;
        }
    }
}

.shared-assignments-pagination ::v-deep {
    .page-item {
        &:not(.active) {
            .page-link {
                background-color: transparent;
            }
        }

        &:hover {
            .page-link {
                border: 1px solid $primary;
            }
        }
    }
}

.shared-assignment-organisation,
.shared-assignment-teacher,
.shared-assignment-category,
.shared-assignment-description,
.shared-assignment-education-levels {
    color: $text-muted-color;
    font-size: 0.8125rem;
    line-height: 1;
    margin-bottom: map-get($grid-gutter-spacing, 'eight');
}

.shared-assignment-category {
    margin-bottom: map-get($grid-gutter-spacing, 'eight');
    line-height: 1.3;
}

.shared-assignment-description {
    line-height: 1.2;
}

//.assignment-info {
//    margin-left: auto;
//}

.show-more-desc {
    width: 100%;
    text-align: right;
    text-decoration: none;
    font-size: $default-font-size;
    padding: 2px 0;
    margin-top: map-get($grid-gutter-spacing, 'five');
}

.loading {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 2rem;
    color: $primary;
    padding-top: map-get($grid-gutter-spacing, 'twenty-one');
    padding-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .shared-assignments-tools {
        flex-direction: row;
        justify-content: space-between;
    }

    .shared-assignment {
        height: 100%;
    }

    div.anonymous-assignment .card-title {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .shared-assignment-body ::v-deep {
        .card-title {
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
        }
    }
}
</style>
